import React, { useMemo, useState } from "react";
import { Grid3x3 } from "lucide-react";

import { formatCurrency, formatInt } from "@/features/relatorio-vendas/lib/formato";

import { DIAS_SEMANA, NOME_FAIXA, formatarDecimal, horasDoMapa, mapaMesasDosDias, ordenar, reaisInteiros } from "../regras";
import { FiltroDias, Secao, Segmentado } from "./Base";
import QuadroCalor from "./QuadroCalor";

/**
 * Mapa mesa × hora (pedido do dono, 27/10/2026): qual mesa rende em qual
 * horário, para decidir a arrumação do salão. Uma linha por mesa (na ordem do
 * número), uma coluna por hora; a cor compara com a melhor mesa e hora do quadro.
 * Faturamento = soma das rodadas pedidas naquela hora; Ocupação = quanto da hora
 * a mesa ficou aberta, em média, nos dias de movimento.
 */
const METRICAS = [
  { key: "fat", rotulo: "Faturamento" },
  { key: "ocupacao", rotulo: "Ocupação" },
];
const num = (v) => Number(v) || 0;
// Ocupação bem pequena não vira "0%": a mesa abriu, só que pouco
const pct = (v) => (v > 0 && v < 0.005 ? "<1%" : `${Math.round(v * 100)}%`);
const horasTxt = (v) => `${formatarDecimal(Math.round(v * 10) / 10)} h`;

export default function MapaMesas({ dados }) {
  const [metrica, setMetrica] = useState("fat");
  const [diasSel, setDiasSel] = useState([]);
  const { porMesa, dias: nDias } = useMemo(() => mapaMesasDosDias(dados.mapa_mesas, diasSel), [dados.mapa_mesas, diasSel]);
  const dias = Math.max(1, nDias);
  const ocup = metrica === "ocupacao";

  const { horas, linhas, total } = useMemo(() => {
    const comMovimento = ordenar(dados.ranking.filter((m) => porMesa[m.id]), "numero", "asc");
    const valor = (c) => (!c ? 0 : ocup ? c.min / (60 * dias) : c.fat);
    const linhas = comMovimento.map((m) => {
      const celulas = porMesa[m.id];
      const lista = Object.values(celulas);
      return {
        key: String(m.id), nome: m.nome, celulas,
        rotulo: <span className={`text-left block ${m.excluida ? "text-muted-foreground" : ""}`}>{m.nome}</span>,
        valores: Object.fromEntries(Object.entries(celulas).map(([h, c]) => [h, valor(c)])),
        fat: lista.reduce((s, c) => s + c.fat, 0),
        min: lista.reduce((s, c) => s + c.min, 0),
      };
    });
    const somaHora = {};
    linhas.forEach((l) => Object.entries(l.celulas).forEach(([h, c]) => {
      const x = somaHora[h] || (somaHora[h] = { fat: 0, min: 0, abertas: 0 });
      x.fat += c.fat; x.min += c.min; x.abertas += c.abertas;
    }));
    const n = Math.max(1, linhas.length);
    const total = {
      key: "total", nome: "Total", celulas: somaHora,
      valores: Object.fromEntries(Object.entries(somaHora).map(([h, c]) => [h, ocup ? c.min / (60 * dias * n) : c.fat])),
      fat: linhas.reduce((s, l) => s + l.fat, 0),
      min: linhas.reduce((s, l) => s + l.min, 0),
    };
    const horas = horasDoMapa(Object.entries(somaHora).map(([h, c]) => ({
      hora: Number(h), rotulo: `${String(h).padStart(2, "0")}h`, total: c.fat + c.min,
    })));
    return { horas, linhas, total };
  }, [dados.ranking, porMesa, ocup, dias]);

  const quais = diasSel.length ? `só ${diasSel.map((d) => DIAS_SEMANA[d]).join(", ")}` : "todos os dias";
  const sub = `${quais} · ${dias} ${dias === 1 ? "dia" : "dias"} de movimento`;

  return (
    <Secao titulo="Mesa × hora" icone={Grid3x3} e2e="mapa-mesas"
      explicacao={ocup
        ? "Quanto de cada hora a mesa ficou ocupada, em média, nos dias de movimento (100% = ocupada a hora inteira, todo dia). Mesa vazia no horário de pico é candidata a mudar de lugar ou de tamanho."
        : "Quanto cada mesa faturou em cada hora. A cor compara com a melhor mesa e hora do quadro: dá para ver qual mesa rende em qual horário."}
      direita={<Segmentado opcoes={METRICAS} valor={metrica} aoMudar={setMetrica} e2e="metrica-mapa-mesas" />}>
      <div className="mb-3"><FiltroDias valor={diasSel} aoMudar={setDiasSel} e2e="dias-mapa-mesas" /></div>
      {!linhas.length
        ? <p className="text-sm text-muted-foreground py-6 text-center">Nenhuma mesa aberta nesses dias da semana.</p>
        : (
          <QuadroCalor key={`${metrica}-${diasSel.join()}`} horas={horas} linhas={linhas} total={total} escala="geral"
            vazio="sem mesa aberta"
            celula={(v) => (ocup ? pct(v) : reaisInteiros(v))}
            totalCelula={(l) => (ocup
              ? { valor: horasTxt(l.min / 60 / dias), sub: "por dia" }
              : { valor: reaisInteiros(l.fat), sub: l.key !== "total" && total.fat > 0 ? pct(l.fat / total.fat) : null })}
            dica={(l, h, f) => {
              const c = l.celulas[h.hora] || {};
              const itens = [["Faturamento", formatCurrency(num(c.fat))]];
              if (l.key === "total") itens.push(["Mesas ocupadas", `${formatarDecimal(Math.round((num(c.min) / 60 / dias) * 10) / 10)} em média`]);
              else itens.push(["Ocupada", `${pct(num(c.min) / (60 * dias))} da hora, em média`]);
              itens.push(["Vezes aberta nessa hora", formatInt(num(c.abertas))]);
              if (num(c.min) > 0) itens.push(["R$ por hora ocupada", formatCurrency(num(c.fat) / (num(c.min) / 60))]);
              itens.push(["Faixa", `${pct(f.pct)} do pico (${NOME_FAIXA[f.faixa]})`]);
              return { sub: `${l.nome} · ${sub}`, itens };
            }}
            dicaTotal={(l) => ({ sub, itens: [
              ["Faturamento", formatCurrency(l.fat)],
              ["Horas ocupada", `${horasTxt(l.min / 60)} (${horasTxt(l.min / 60 / dias)} por dia)`],
              ...(l.min > 0 ? [["R$ por hora ocupada", formatCurrency(l.fat / (l.min / 60))]] : []),
            ] })}
            legenda={ocup ? "Cor = % da hora mais ocupada do quadro:" : "Cor = % da melhor mesa e hora do quadro:"} />
        )}
    </Secao>
  );
}
